<template>
  <div class="group-weighing-panel">
    <panel-section v-if="summary">
      <group-weighing-summary :summary="summary" />
    </panel-section>

    <panel-section v-if="canWeigh">
      <weight-alert v-if="shortAlert" :alert="shortAlert" />
      <p v-if="weightError" class="group-weighing-panel__error group-weighing-panel__error--form">{{ weightError }}</p>
      <real-weight-form
        :group-id="group.meal_id + '-' + group.subgroup_id"
        :minimum-grams="group.theoretical_weight_grams"
        :ingredients="ingredients"
        :loading="weighing"
        @submit-real-weight="onSubmitRealWeight"
      />
    </panel-section>

    <panel-section v-if="isZeroWeight">
      <p class="group-weighing-panel__text">{{ texts.NOT_WEIGHABLE_DESCRIPTION }}</p>
    </panel-section>

    <panel-section v-if="ingredients.length > 0" :title="texts.INGREDIENTS_TITLE" :help="texts.INGREDIENTS_HELP">
      <detail-table :rows="ingredientRows" is-muted-value />
    </panel-section>

    <p v-if="actionError" class="group-weighing-panel__error">{{ actionError }}</p>
  </div>
</template>

<script>
import CookedWeightsGateway from '@/gateways/CookedWeightsGateway'
import PanelSection from './PanelSection.vue'
import { isWeighableGroup, isZeroWeightGroup } from '@/utils/mealServingTotals'
import WeightAlert from './WeightAlert.vue'
import RealWeightForm from './RealWeightForm.vue'
import DetailTable from './DetailTable.vue'
import GroupWeighingSummary from './GroupWeighingSummary.vue'
import { buildWeighingSummary } from '../weighingSummary'
import { listGroupIngredients } from '@/utils/groupIngredients'
import { buildIngredientRows } from '../groupDetailRows'
import { buildShortWeightAlert } from '../shortWeightAlert'
import { STATUS, BACKEND_REASON, HTTP_STATUS, TEXTS, ERROR_TEXT_BY_REASON } from '../cookedWeightsConstants'

export default {
  name: 'GroupWeighingPanel',
  components: {
    PanelSection,
    WeightAlert,
    RealWeightForm,
    DetailTable,
    GroupWeighingSummary
  },
  props: {
    date: {
      type: String,
      required: true
    },
    group: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      weighing: false,
      weightError: '',
      actionError: ''
    }
  },
  computed: {
    texts () {
      return TEXTS
    },
    ingredients () {
      return listGroupIngredients(this.group)
    },
    summary () {
      return buildWeighingSummary(this.group)
    },
    ingredientRows () {
      return buildIngredientRows(this.ingredients)
    },
    isShortWeighed () {
      return this.group.status === STATUS.SHORT_WEIGHED
    },
    isWeighable () {
      return isWeighableGroup(this.group)
    },
    isZeroWeight () {
      return isZeroWeightGroup(this.group)
    },
    canWeigh () {
      return this.isWeighable && (this.group.status === STATUS.NO_RECORD || this.isShortWeighed)
    },
    shortAlert () {
      return buildShortWeightAlert(this.group)
    }
  },
  methods: {
    onSubmitRealWeight (grams) {
      this.weighing = true
      this.resetErrors()
      CookedWeightsGateway
        .storeRealWeight(this.date, this.group.meal_id, this.group.subgroup_id, grams)
        .then((response) => this.$emit('group-updated', response.data.group))
        .catch((error) => this.handleWeightError(error))
        .finally(() => { this.weighing = false })
    },
    handleWeightError (error) {
      const data = error.response && error.response.data ? error.response.data : {}
      if (this.isShortWeightResponse(error, data)) {
        this.$emit('group-updated', data.group)
        return
      }
      if (this.hasFormatErrors(error, data)) {
        this.weightError = data.errors.real_weight_grams[0]
        return
      }
      this.showError(error, data)
    },
    isShortWeightResponse (error, data) {
      return this.statusOf(error) === HTTP_STATUS.UNPROCESSABLE && data.reason === BACKEND_REASON.SHORT_WEIGHT
    },
    hasFormatErrors (error, data) {
      return this.statusOf(error) === HTTP_STATUS.UNPROCESSABLE &&
        data.errors && data.errors.real_weight_grams
    },
    messageFor (data) {
      return data.message || ERROR_TEXT_BY_REASON[data.reason] || TEXTS.ERROR_FALLBACK
    },
    showError (error, data) {
      this.actionError = this.messageFor(data)
      if (this.statusOf(error) === HTTP_STATUS.CONFLICT) {
        this.$emit('reload-requested')
      }
    },
    statusOf (error) {
      return error.response ? error.response.status : 0
    },
    resetErrors () {
      this.weightError = ''
      this.actionError = ''
    }
  }
}
</script>

<style scoped>
.group-weighing-panel__text {
  margin-bottom: 0;
  font-size: var(--font-base);
  color: var(--text-strong);
}

.group-weighing-panel__error {
  margin: 12px 0 0;
  font-size: var(--font-small);
  color: var(--danger-text);
}

.group-weighing-panel__error--form {
  margin: 0 0 12px;
  font-weight: 600;
}
</style>
